import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  ButtonText,
  Center,
  HStack,
  Spinner,
  Switch,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import type { ReceivedDataMessage } from '@livekit/components-core';
import { useDataChannel, useRoomContext } from '@livekit/components-react';
import { Track, type Participant } from 'livekit-client';
import {
  Mic, MicOff, Video, VideoOff,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { OralExamErrorState } from '@/common/components/oralExam/OralExamErrorState';
import { OralExamTransitionProgressDialog } from '@/common/components/oralExam/OralExamTransitionProgressDialog';
import { MeetingPublishCameraGuard } from '@/common/components/meeting/MeetingPublishCameraGuard';
import { MeetingSessionShell } from '@/common/components/meeting/MeetingSessionShell';
import { MediaPill } from '@/common/components/meeting/MediaPill';
import type { MeetingJoinSettings } from '@/common/components/meeting/types';
import {
  BriefingStudentAudioButton,
  BriefingStudentMutedIndicator,
} from '@/common/components/oralExam/BriefingParticipantControls';
import {
  BriefingConferenceStage,
  encodeBriefingDeparturePayload,
  isLecturerParticipant,
  ORAL_EXAM_BRIEFING_TOPIC,
  parseBriefingDeparturePayload,
  type BriefingDepartureMessageType,
  type SpeakPermissionPayload,
} from '@/common/components/oralExam/BriefingConferenceStage';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import {
  VerticalFlowStepper,
  type FlowStepDescriptor,
} from '@/common/components/oralExam/VerticalFlowStepper';
import { getOralExamCohortState } from '@/common/api/endpoints/oral-exams/oral-exams';
import { useOralExamCohortState } from '@/common/hooks/oralExam/useOralExamCohortState';
import { unwrapOralExamCohortState } from '@/common/hooks/oralExam/unwrapOralExamCohortState';
import { useOralExamIdReview } from '@/common/hooks/oralExam/useOralExamIdReview';

export type LecturerGroupBriefingStageProps = {
  slotId: string;
  capacity: number;
  etag?: string | null;
  joinSettings: MeetingJoinSettings;
  onClose: () => void;
  joinCreds: { token: string; livekitUrl: string; roomName?: string } | null;
  joinError: Error | null;
  refetch: () => void;
  isLoading: boolean;
  /** Ends cohort-token quiet remint wait once LiveKit connects. */
  onRoomTokenConnected?: () => void;
};

function LecturerBriefingShell({
  approvedCount,
  children,
}: {
  approvedCount: number;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  const steps: FlowStepDescriptor[] = useMemo(() => [
    {
      key: 'idReview',
      label: t('oralExam.lecturer.groupBriefing.steps.idReview', {
        defaultValue: 'ID review',
      }),
      subtitle: t('oralExam.lecturer.groupBriefing.steps.idReviewSubtitle', {
        defaultValue: '{{count}} students accepted',
        count: approvedCount,
      }),
      status: 'done',
    },
    {
      key: 'avatarPhoto',
      label: t('oralExam.lecturer.groupBriefing.steps.avatarPhoto', {
        defaultValue: 'Avatar photo',
      }),
      status: 'done',
    },
    {
      key: 'groupBriefing',
      label: t('oralExam.lecturer.groupBriefing.steps.groupBriefing', {
        defaultValue: 'Group briefing',
      }),
      subtitle: t('oralExam.lecturer.groupBriefing.steps.liveToStudents', {
        defaultValue: 'live to students',
      }),
      status: 'active',
    },
    {
      key: 'exam',
      label: t('oralExam.lecturer.groupBriefing.steps.exam', {
        defaultValue: 'Exam',
      }),
      subtitle: t('oralExam.lecturer.groupBriefing.steps.examSubtitle', {
        defaultValue: 'starts after briefing',
      }),
      status: 'upcoming',
    },
  ], [approvedCount, t]);

  return (
    <Box flex={1} bg="$bgApp" testID="lecturer-briefing-shell">
      <HStack flex={1} space="lg" p="$6" alignItems="flex-start">
        <Box width={286} flexShrink={0}>
          <VerticalFlowStepper
            testID="lecturer-briefing-stepper"
            steps={steps}
            density="comfortable"
          />
        </Box>

        <Box
          flex={1}
          maxWidth={854}
          alignSelf="stretch"
          bg="$bgCard"
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$xl"
          overflow="hidden"
          testID="lecturer-briefing-card"
        >
          {children}
        </Box>
      </HStack>
    </Box>
  );
}

type BriefingDepartureCardsProps = {
  students: Array<{ bookingId: string; name: string }>;
  devicesOff: boolean;
};

function BriefingDepartureCards({ students, devicesOff }: BriefingDepartureCardsProps) {
  const { t } = useTranslation();

  return (
    <VStack flex={1} space="md" testID="briefing-departure-stage">
      <VStack space="xs">
        <Text size="lg" color="$textHeading" fontWeight="bold">
          {t('oralExam.lecturer.groupBriefing.departureTitle', {
            defaultValue: 'Starting the exam',
          })}
        </Text>
        <Text size="sm" color="$textBody">
          {devicesOff
            ? t('oralExam.lecturer.groupBriefing.departureDevicesOff', {
              defaultValue: 'Your camera and microphone are off.',
            })
            : t('oralExam.lecturer.groupBriefing.departurePendingSubtitle', {
              defaultValue: 'Preparing to start…',
            })}
        </Text>
      </VStack>
      <HStack space="sm" flexWrap="wrap">
        {students.map((student) => (
          <Box
            key={student.bookingId}
            width={200}
            p="$3"
            borderWidth={1}
            borderColor="$borderLight"
            borderRadius="$md"
            bg="$bgSubtle"
            testID={`briefing-departure-starting-card-${student.bookingId}`}
          >
            <Text size="sm" color="$textHeading" fontWeight="semibold" numberOfLines={2}>
              {student.name}
            </Text>
            <Text size="sm" color="$textSubtle" mt="$1">
              {t('oralExam.lecturer.groupBriefing.departureStartingCard', {
                defaultValue: 'Starting…',
              })}
            </Text>
          </Box>
        ))}
      </HStack>
    </VStack>
  );
}

type BriefingRoomControlsProps = {
  slotId: string;
  capacity: number;
  approvedCount: number;
  etag?: string | null;
};

function BriefingRoomControls({
  slotId,
  capacity,
  approvedCount,
  etag,
}: BriefingRoomControlsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const room = useRoomContext();
  const {
    cohort,
    bookingRuns,
    queryKey,
    etag: polledEtag,
  } = useOralExamCohortState(slotId, { polling: true });
  // Always prefer the FRESH polled slot etag over the `etag` prop captured before
  // this stage mounted. Opening the group briefing mutates the slot and bumps its
  // etag, so the prop is stale by the time "Start the exam" fires — sending it
  // makes start-cohort fail 412 forever (the 412 handler refetches state, but this
  // component would keep resending the stale prop). The polled etag tracks every
  // slot mutation, so start succeeds and 412 recovery actually converges.
  const effectiveEtag = polledEtag ?? etag;
  const { startCohort, isStarting } = useOralExamIdReview({ etag: effectiveEtag, stateQueryKey: queryKey });
  const [muteAllDefault, setMuteAllDefault] = useState(true);
  const [speakAllowedByIdentity, setSpeakAllowedByIdentity] = useState<Record<string, boolean>>({});
  const [transitionOpen, setTransitionOpen] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [isAwaitingStart, setIsAwaitingStart] = useState(false);
  const [departureActive, setDepartureActive] = useState(false);
  const [devicesOff, setDevicesOff] = useState(false);
  const preDevicesRef = useRef<{ camera: boolean; microphone: boolean } | null>(null);
  const startingRepeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isStartPending = isAwaitingStart || isStarting;

  const admittedBookingIds = useMemo(
    () => bookingRuns
      .filter((run) => run.isAdmitted || run.idVerification.status === 'approved')
      .map((run) => run.bookingId),
    [bookingRuns],
  );

  const stopStartingRepeat = useCallback(() => {
    if (startingRepeatRef.current) {
      clearInterval(startingRepeatRef.current);
      startingRepeatRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    stopStartingRepeat();
  }, [stopStartingRepeat]);

  const publishDeparture = useCallback((type: BriefingDepartureMessageType) => {
    room.localParticipant.publishData(
      encodeBriefingDeparturePayload(type),
      { topic: ORAL_EXAM_BRIEFING_TOPIC, reliable: true },
    );
  }, [room]);

  const disableDevices = useCallback(() => {
    const { localParticipant } = room;
    if (!preDevicesRef.current) {
      preDevicesRef.current = {
        camera: localParticipant.isCameraEnabled,
        microphone: localParticipant.isMicrophoneEnabled,
      };
    }
    setDevicesOff(true);
    localParticipant.setCameraEnabled(false).catch(() => undefined);
    localParticipant.setMicrophoneEnabled(false).catch(() => undefined);
  }, [room]);

  const restoreDevices = useCallback(() => {
    const saved = preDevicesRef.current;
    if (!saved) return;
    const { localParticipant } = room;
    localParticipant.setCameraEnabled(saved.camera).catch(() => undefined);
    localParticipant.setMicrophoneEnabled(saved.microphone).catch(() => undefined);
    preDevicesRef.current = null;
  }, [room]);

  const beginStartingRepeat = useCallback(() => {
    publishDeparture('starting');
    stopStartingRepeat();
    startingRepeatRef.current = setInterval(() => {
      publishDeparture('starting');
    }, 5000);
  }, [publishDeparture, stopStartingRepeat]);

  const enterDeparture = useCallback(() => {
    setDepartureActive(true);
  }, []);

  const exitDeparture = useCallback(() => {
    setDepartureActive(false);
    setDevicesOff(false);
    restoreDevices();
  }, [restoreDevices]);

  const publishSpeakPermission = useCallback((targetIdentity: string, allowed: boolean) => {
    const payload: SpeakPermissionPayload = {
      type: 'oralExam.speakPermission',
      targetIdentity,
      allowed,
    };
    room.localParticipant.publishData(
      new TextEncoder().encode(JSON.stringify(payload)),
      { topic: ORAL_EXAM_BRIEFING_TOPIC, reliable: true },
    );
  }, [room]);

  const handleAllowToSpeakToggle = useCallback((identity: string, allowed: boolean) => {
    if (allowed) setMuteAllDefault(false);
    setSpeakAllowedByIdentity((prev) => ({ ...prev, [identity]: allowed }));
    publishSpeakPermission(identity, allowed);
  }, [publishSpeakPermission]);

  const handleMuteAllToggle = useCallback((enabled: boolean) => {
    setMuteAllDefault(enabled);
    if (enabled) {
      setSpeakAllowedByIdentity((prev) => {
        Object.entries(prev).forEach(([identity, allowed]) => {
          if (allowed) publishSpeakPermission(identity, false);
        });
        return {};
      });
    }
  }, [publishSpeakPermission]);

  const onBriefingData = useCallback((msg: ReceivedDataMessage) => {
    if (!msg.from || !isLecturerParticipant(msg.from)) return;

    const departure = parseBriefingDeparturePayload(msg.payload);
    if (departure) {
      if (departure.type === 'starting') {
        if (!departureActive) {
          enterDeparture();
        }
        // Mute with the Starting cards. The presser's own publish does not
        // echo, so handleStartInExam mutes too. Repeats must not recapture
        // the already-muted devices as the restore snapshot.
        disableDevices();
        return;
      }
      if (departure.type === 'cancelled') {
        stopStartingRepeat();
        exitDeparture();
        return;
      }
      if (departure.type === 'started') {
        stopStartingRepeat();
        disableDevices();
      }
      return;
    }

    try {
      const parsed = JSON.parse(new TextDecoder().decode(msg.payload)) as Partial<SpeakPermissionPayload>;
      if (parsed?.type !== 'oralExam.speakPermission' || !parsed.targetIdentity) return;
      if (parsed.allowed) setMuteAllDefault(false);
      setSpeakAllowedByIdentity((prev) => ({
        ...prev,
        [parsed.targetIdentity!]: Boolean(parsed.allowed),
      }));
    } catch {
      /* ignore malformed packets */
    }
  }, [departureActive, disableDevices, enterDeparture, exitDeparture, stopStartingRepeat]);

  useDataChannel(ORAL_EXAM_BRIEFING_TOPIC, onBriefingData);

  const handleStartInExam = useCallback(async () => {
    if (admittedBookingIds.length === 0 || isStartPending) return;
    if (departureActive) return;

    setTransitionError(null);
    setTransitionOpen(true);
    setIsAwaitingStart(true);

    beginStartingRepeat();
    enterDeparture();
    // Cut camera and mic in the same turn as the modal. Do not await: the
    // `starting` publish above must stay ahead of anything slow, and Start
    // itself must not wait on the unpublish. Failure restores both.
    disableDevices();

    try {
      await startCohort({
        slotId,
        data: { admittedBookingIds, partialStart: false },
        etag: effectiveEtag,
      });
      stopStartingRepeat();
      disableDevices();
      publishDeparture('started');
      setTransitionOpen(false);
    } catch (error) {
      stopStartingRepeat();
      let stillBriefing = false;
      try {
        const fresh = await queryClient.fetchQuery({
          queryKey,
          queryFn: async () => (await getOralExamCohortState(slotId)).data,
        });
        const freshCohort = unwrapOralExamCohortState(fresh);
        stillBriefing = freshCohort?.sessionPhase === OralExamSessionPhase.group_briefing;
      } catch {
        stillBriefing = false;
      }
      if (stillBriefing) {
        publishDeparture('cancelled');
        exitDeparture();
        setTransitionError(t('oralExam.lecturer.groupBriefing.startFailed', {
          defaultValue: 'Could not start the exam. Try again.',
        }));
      } else {
        setTransitionOpen(false);
      }
    } finally {
      setIsAwaitingStart(false);
    }
  }, [
    admittedBookingIds,
    beginStartingRepeat,
    departureActive,
    disableDevices,
    effectiveEtag,
    enterDeparture,
    exitDeparture,
    isStartPending,
    publishDeparture,
    queryClient,
    queryKey,
    slotId,
    startCohort,
    stopStartingRepeat,
    t,
  ]);

  const handleCloseTransition = useCallback(() => {
    if (isStartPending) return;
    setTransitionOpen(false);
    setTransitionError(null);
  }, [isStartPending]);

  const departureStudents = useMemo(
    () => bookingRuns
      .filter((run) => run.isAdmitted || run.idVerification.status === 'approved')
      .map((run) => ({
        bookingId: run.bookingId,
        name: run.studentName ?? run.bookingId,
      })),
    [bookingRuns],
  );

  const renderTileOverlay = useCallback((participant: Participant) => {
    const allowed = speakAllowedByIdentity[participant.identity]
      ?? participant.isMicrophoneEnabled;
    return (
      <BriefingStudentMutedIndicator
        identity={participant.identity}
        muted={!allowed}
      />
    );
  }, [speakAllowedByIdentity]);

  const renderTileControl = useCallback((participant: Participant) => {
    const allowed = speakAllowedByIdentity[participant.identity]
      ?? participant.isMicrophoneEnabled;
    return (
      <BriefingStudentAudioButton
        identity={participant.identity}
        muted={!allowed}
        onToggle={(muted) => handleAllowToSpeakToggle(participant.identity, !muted)}
      />
    );
  }, [handleAllowToSpeakToggle, speakAllowedByIdentity]);

  return (
    <MeetingPublishCameraGuard enabled={!departureActive}>
      <Box flex={1} position="relative">
        <LecturerBriefingShell approvedCount={approvedCount}>
          <VStack flex={1} space="md" p="$4" overflow="hidden" testID="briefing-room-controls">
            <VStack space="xs">
              <Text size="lg" color="$textHeading" fontWeight="bold" testID="briefing-room-title">
                {t('oralExam.lecturer.groupBriefing.roomTitle', {
                  defaultValue: 'Group briefing',
                })}
              </Text>
              <Text size="sm" color="$textBody">
                {t('oralExam.lecturer.groupBriefing.roomSubtitle', {
                  defaultValue: 'Give final instructions, then start the exam.',
                })}
              </Text>
            </VStack>

            <HStack
              alignItems="center"
              space="sm"
              bg="$statusWarningSubtle"
              px="$3"
              py="$2"
              borderRadius="$md"
              alignSelf="flex-start"
              testID="briefing-live-indicator"
            >
              <Box w={8} h={8} borderRadius="$full" bg="$statusWarning" />
              <Text size="sm" color="$textBody" fontWeight="semibold">
                {t('oralExam.lecturer.groupBriefing.liveToStudents', {
                  defaultValue: 'Live to {{count}} students',
                  count: capacity,
                })}
              </Text>
            </HStack>

            <Box flex={1} minHeight={180} position="relative" testID="briefing-room-stage">
              <Box
                flex={1}
                minHeight={180}
                opacity={departureActive ? 0 : 1}
                pointerEvents={departureActive ? 'none' : 'auto'}
                aria-hidden={departureActive}
                position={departureActive ? 'absolute' : 'relative'}
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
              >
                <BriefingConferenceStage
                  renderTileOverlay={renderTileOverlay}
                  renderTileControl={renderTileControl}
                  viewerRole="lecturer"
                  slotId={slotId}
                  brieferId={cohort?.brieferId}
                  slotOwnerId={cohort?.slotOwnerId}
                />
              </Box>
              {departureActive ? (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  flex={1}
                  bg="$bgCard"
                >
                  <BriefingDepartureCards students={departureStudents} devicesOff={devicesOff} />
                </Box>
              ) : null}
            </Box>

            <HStack space="md" alignItems="center" flexWrap="wrap">
              <MediaPill
                source={Track.Source.Microphone}
                kind="audioinput"
                enabledIcon={Mic}
                disabledIcon={MicOff}
                testID="meeting-mic-toggle"
              />
              <MediaPill
                source={Track.Source.Camera}
                kind="videoinput"
                enabledIcon={Video}
                disabledIcon={VideoOff}
                testID="meeting-cam-toggle"
              />
              <HStack alignItems="center" space="sm" testID="briefing-mute-all-toggle">
                <Switch
                  value={muteAllDefault}
                  onValueChange={handleMuteAllToggle}
                  testID="briefing-mute-all-switch"
                  accessibilityLabel={t('oralExam.lecturer.groupBriefing.muteAllDefault', {
                    defaultValue: 'Mute all',
                  })}
                />
                <Text size="sm" color="$textBody">
                  {t('oralExam.lecturer.groupBriefing.muteAllDefault', {
                    defaultValue: 'Mute all',
                  })}
                </Text>
              </HStack>
            </HStack>

            <HStack justifyContent="flex-end">
              <Button
                action="primary"
                onPress={() => { handleStartInExam(); }}
                isDisabled={isStartPending || departureActive || admittedBookingIds.length === 0}
                testID="briefing-start-in-exam"
              >
                <ButtonText>
                  {t('oralExam.lecturer.groupBriefing.startExam', { defaultValue: 'Start the exam' })}
                </ButtonText>
              </Button>
            </HStack>

          </VStack>
        </LecturerBriefingShell>
        <OralExamTransitionProgressDialog
          isOpen={transitionOpen}
          variant="startExam"
          isPending={isStartPending && !transitionError}
          error={transitionError}
          onRetry={() => { handleStartInExam(); }}
          onClose={handleCloseTransition}
        />
      </Box>
    </MeetingPublishCameraGuard>
  );
}

/**
 * Group briefing stage: lecturer broadcasts A/V to admitted students before the exam.
 * Avatar/device setup happens earlier in LecturerPresenceSetupGate (before room join).
 */
export function LecturerGroupBriefingStage({
  slotId,
  capacity,
  etag,
  joinSettings,
  onClose,
  joinCreds,
  joinError,
  refetch,
  isLoading,
  onRoomTokenConnected,
}: LecturerGroupBriefingStageProps) {
  const { t } = useTranslation();
  const { bookingRuns } = useOralExamCohortState(slotId, { polling: true });

  const approvedCount = useMemo(
    () => bookingRuns.filter((run) => run.idVerification.status === 'approved').length,
    [bookingRuns],
  );

  if (joinError) {
    return (
      <OralExamErrorState
        title={t('oralExam.errorState.joinFailedTitle', {
          defaultValue: 'Could not join the exam room',
        })}
        message={t('oralExam.joinFailed')}
        retry={refetch}
        leave={onClose}
        leaveLabel={t('oralExam.errorState.leaveBriefing', { defaultValue: 'Leave briefing' })}
      />
    );
  }

  if (!joinCreds && isLoading) {
    return (
      <Center flex={1} p="$4">
        <Spinner size="large" />
        <Text size="sm" color="$textSubtle" mt="$2">{t('oralExam.connecting')}</Text>
      </Center>
    );
  }

  if (!joinCreds) {
    return (
      <OralExamErrorState
        title={t('oralExam.errorState.joinFailedTitle', {
          defaultValue: 'Could not join the exam room',
        })}
        message={t('oralExam.joinFailed')}
        retry={refetch}
        leave={onClose}
        leaveLabel={t('oralExam.errorState.leaveBriefing', { defaultValue: 'Leave briefing' })}
      />
    );
  }

  return (
    <MeetingSessionShell
      token={joinCreds.token}
      serverUrl={joinCreds.livekitUrl}
      roomName={joinCreds.roomName}
      participantRole="lecturer"
      lobbyDone
      joinSettings={joinSettings}
      onLobbyJoin={() => {}}
      onLeave={onClose}
      onRejoin={refetch}
      onConnected={onRoomTokenConnected}
    >
      <BriefingRoomControls
        slotId={slotId}
        capacity={capacity}
        approvedCount={approvedCount}
        etag={etag}
      />
    </MeetingSessionShell>
  );
}
